// src/hooks/useBreedInsightsOpenAI.js
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Calls /api/breed-insights to get up-to-date items per breed (summarized by OpenAI).
 * Debounces calls and cancels outdated responses.
 */
export default function useBreedInsightsOpenAI(breeds = [], opts = {}) {
  const { perBreedLimit = 5, refreshMs = 15 * 60 * 1000 } = opts;
  const [byBreed, setByBreed] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  const cleanBreeds = useMemo(
    () => Array.from(new Set((breeds || []).filter(Boolean))),
    [breeds]
  );

  const fetchNow = async () => {
    if (!cleanBreeds.length) {
      setByBreed({});
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setLoading(true);
    setErr('');
    try {
      const res = await fetch('/api/breed-insights', {
        method: 'POST',
        signal: ac.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          breeds: cleanBreeds.slice(0, 40),
          perBreedLimit,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to fetch insights');
      setByBreed(json || {});
    } catch (e) {
      if (e.name !== 'AbortError') setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNow(); // initial
    if (timerRef.current) clearInterval(timerRef.current);
    if (refreshMs > 0) {
      timerRef.current = setInterval(fetchNow, refreshMs);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanBreeds.join('|'), perBreedLimit, refreshMs]);

  return { byBreed, loading, err, refetch: fetchNow };
}
